import React, { Suspense, useEffect } from 'react';
import * as ReactDOM from 'react-dom/client';
import { createPortal } from 'react-dom';
import {
  Routes,
  Route,
  BrowserRouter,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ConfigProvider, message, App, theme } from 'antd';
import { observer } from 'mobx-react-lite';
import MainPage from '@src/pages/index';
import Error from '@src/components/notice-page';
import InitHolder from '@src/components/init-holder';
import globalStore from '@src/store/global'; // 全局 store
import userStore from '@src/store/user'; // 用户 store
import EE from '@src/common/event';
import { useBroadcast } from '@src/hooks/useBroadcast';
import { StyleProvider } from '@ant-design/cssinjs';
import appLogger from "@public/log/monitor";
import "@public/urm";
import TimeLogger from "@public/log/time-logger";
import { HelperCore } from './common/helper';
import SearchHomePage from './pages/search-home';
import TeamPage from './pages/team-home';
import ChatPage from './pages/chat';
import ChatWS from './pages/chat-ws';
import chatReadStore from './store/chat/chatRead';
import ToolsPage from './pages/tools';
import SidebarNav from './components/sidebar-nav';
import AgentPage from './pages/agent-home';
import ChatAgent from '@src/pages/chat-agent'
import AgentCreate from './pages/agent-create'
import client from './store/client';
import ExamplesPage from './pages/examples';
import FullScreenModal from './components/full-screen-modal';
import SwiperCore, { Pagination, Navigation } from 'swiper';

const GenerateImagePage = React.lazy(() => import('@src/pages/generate-image'));
const GenerateVideoPage = React.lazy(() => import('@src/pages/generate-video'));
const ExpandImagePage = React.lazy(() => import('@src/pages/modify-image/expand'));
const EraseImagePage = React.lazy(() => import('@src/pages/modify-image/erase'));
const RepaintImagePage = React.lazy(() => import('@src/pages/modify-image/repaint'));
const SharpenImage = React.lazy(() => import('@src/pages/modify-image/sharpen'))
const ImageMatting = React.lazy(() => import('@src/pages/modify-image/matting'))
const SquarePage = React.lazy(() => import('@src/pages/square'));
const SquareImagePage = React.lazy(() => import('@src/pages/square-image'));
const SquareVideoPage = React.lazy(() => import('@src/pages/square-video'));
const SquareAgentPage = React.lazy(() => import('@src/pages/square-agent'));
const WorksPage = React.lazy(() => import('@src/pages/works'));
const ChatKnowledge = React.lazy(() => import('@src/pages/chat-knowledge'));
const Agreement = React.lazy(() => import('@src/pages/agreement'));
const CreateRobotPage = React.lazy(() => import('@src/pages/create-robot'));
const AgentShare = React.lazy(() => import('@src/pages/agent-share'));

// swiper ---start---
import 'swiper/css'
import 'swiper/css/pagination'

SwiperCore.use([Navigation, Pagination])
// swiper ---end---

// 智能体分享页拦截处理：非桌面端 -> 0508智能体分享落地页
// 拦截路径 /tools/aiagent/chat/<agentId> 并携带 query 参数 share=1 并跳到 /tools/aiagent/share?redirecto=<agentId>
if (
  !(client.isLLQ || client.isSeLLQ) // 客户端不走本逻辑
  && location.pathname.startsWith('/tools/aiagent/chat/')
  && location.search.includes('share=1')
) {
  location.href = `/tools/aiagent/share?agentid=${location.pathname.split('/').pop()}`
}

// 预加载agent表单 ---start---
window.React = React;
window.ReactDOM = {
  createPortal,
  ...ReactDOM,
}
const IS_PROD = import.meta.env.MODE === 'master'
const UNI_AGENT_URL = IS_PROD
  ? 'https://s.ssl.qhres2.com/pkg/qiwoo/uni-agent/1.0.0/uni-agent.min.js'
  : 'https://s.ssl.qhres2.com/pkg/qiwoo/uni-agent/0.1.1/uni-agent.min.js'
HelperCore.loadJsExist(UNI_AGENT_URL, 'UNI_AGENT_JS', () => {
  // console.info('uni-agent-js 加载成功')
})
window.addEventListener('click', (e) => {
  EE.emit('hide-agent-prompt-form', e)
})
// 预加载agent表单 ---end---

// 兼容 antd5.0 :where
const SupportLowerBrowserForAntdWhere = true;
const { darkAlgorithm, defaultAlgorithm } = theme;

message.config({
  duration: 2,
  maxCount: 3,
});

if (window.localforage) {
  window.localforage.config({
    name: 'nami_ai',
    driver: [window.localforage.INDEXEDDB, window.localforage.LOCALSTORAGE],
  });
}

new TimeLogger("all_page_time_logger"); // 页面时长打点

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();

  globalStore.setData('navigate', (url, ...props) => {
    navigate(HelperCore.repaireUrl(url), ...props);
  });

  // 多 tab 同步登录状态
  useBroadcast("user-status-changed", (userInfo) => {
    if (userInfo?.id != userStore.uid) {
      globalStore.reloadPage();
    }
  });

  // antd 5.0 兼容低版本浏览器
  const { modal } = App.useApp();
  useEffect(() => {
    globalStore.setData("AntdModal", modal);
  }, []);

  // 死循环已停止
  useEffect(() => {
    const timerReload = setTimeout(() => {
      globalStore.setUserConfig({
        reloadCount: 0,
      });
    }, 5000);

    return () => {
      clearTimeout(timerReload);
    }
  }, []);

  useEffect(() => {
    // 设置fromside基参
    let fromside = 'web'
    if(client.isClient) {
      fromside = 'weishi'
      appLogger.setBaseData({ fromside })
      client.run("GetSystemInfo", ({standalone}) => {
        if(standalone == 1) {
          fromside = 'client'
          appLogger.setBaseData({ fromside })
        }
      })
    } else {
      if (client.isLLQ) {
        fromside = 'llq';
      }
      appLogger.setBaseData({ fromside })
    }
  }, [client])

  useEffect(() => {
    // pv 打点，切路由时
    appLogger.getTrack();
    // 访问时长打点
    EE.emit("all_page_time_logger", {
      _: HelperCore.uniqueId(),
    });
    // 停止朗读
    try {
      chatReadStore.readStore.stop();
    } catch (e) { }
    // 卫士字号
    HelperCore[location.pathname == "/ws" ? "addClass" : "removeClass"](document.querySelector("html"), "page-normal-size");

    EE.emit("location-changed");
    globalStore.setShowChildIFrameModal(false);

    //记录上一个页面的路由
    return () => {
      globalStore.setPreviousRoute(location);
    }
  }, [location]);

  return null;
};

export default observer(function Application() {
  return (
    <Suspense fallback={null}>
      <StyleProvider hashPriority={SupportLowerBrowserForAntdWhere ? "high" : "low"}>
        <ConfigProvider
          theme={{
            token: {
              colorText: 'var(--text-color-primary)',
            },
            components: {
              Modal: {},
              Select: {
                colorPrimaryHover: '#000',    // 悬停/聚焦边框颜色
                colorPrimary: '#000',         // 选中项边框颜色（如多选标签）
              },
              Checkbox: {
                colorPrimaryHover: '#000',    // 悬停/聚焦边框颜色
                colorPrimary: '#000',         // 选中项边框颜色（如多选标签）
              },
              Radio: {
                colorPrimaryHover: '#000',    // 悬停/聚焦边框颜色
                colorPrimary: '#000',         // 选中项边框颜色（如多选标签）
              }
            },
            algorithm: globalStore.isDark ? darkAlgorithm : defaultAlgorithm,
          }}
        >
          <App>
            <BrowserRouter>
              <Header />
              <FullScreenModal />
              <Routes>
                <Route path="/" element={<InitHolder><MainPage /></InitHolder>}>
                  <Route index element={<SearchHomePage />} />
                  <Route path="teamhome" element={<TeamPage />} />
                  <Route path="chat" element={<ChatPage />} />
                  <Route path="chat/:id" element={<ChatPage />} />
                  <Route path="aichat" element={<ChatPage />} />
                  <Route path="aichat/:id" element={<ChatPage />} />
                  <Route path="agentchat" element={<ChatPage />} />
                  <Route path="agentchat/:id" element={<ChatPage />} />
                  <Route path="examples" element={<ExamplesPage />} />
                  <Route path="create" element={<CreateRobotPage />} />
                  <Route path="square">
                    <Route index element={<SquarePage />} />
                    <Route path="video" element={<><SidebarNav key='sidebar-nav' /><SquareVideoPage /></>} />
                    <Route path="effect" element={<><SidebarNav key='sidebar-nav' /><SquareVideoPage /></>} />
                    <Route path="image" element={<><SidebarNav key='sidebar-nav' /><SquareImagePage /></>} />
                    <Route path="agent" element={<SquareAgentPage />} />
                  </Route>
                  <Route path="tools">
                    <Route path="aiagent/*" element={<AgentPage />}></Route>
                    <Route path="aiagent/manage" element={<AgentCreate />}></Route>
                    <Route path="aiagent/chat/:agentId" element={<ChatAgent />}></Route>
                    <Route path=":type" element={<ToolsPage />}></Route>
                    <Route path="aiagent/share" element={<AgentShare />}></Route>
                  </Route>
                </Route>
                <Route path="works">
                  <Route path="video" element={<div className='flex size-full'><SidebarNav key='sidebar-nav' /><WorksPage /></div>} />
                  <Route path="image" element={<div className='flex size-full'><SidebarNav key='sidebar-nav' /><WorksPage /></div>} />
                </Route>
                <Route path="generate">
                  <Route path="video" element={<div className='flex size-full'><SidebarNav key='sidebar-nav' /><GenerateVideoPage /></div>} />
                  <Route path="image" element={<div className='flex size-full'><SidebarNav key='sidebar-nav' /><GenerateImagePage /></div>} />
                  <Route path="image/expand" element={<div className='flex size-full'><SidebarNav key='sidebar-nav' /><ExpandImagePage /></div>} />
                  <Route path="image/erase" element={<div className='flex size-full'><SidebarNav key='sidebar-nav' /><EraseImagePage /></div>} />
                  <Route path="image/repaint" element={<div className='flex size-full'><SidebarNav key='sidebar-nav' /><RepaintImagePage /></div>} />
                  <Route path="image/sharpen" element={<div className='flex size-full'><SidebarNav key='sidebar-nav' /><SharpenImage /></div>} />
                  <Route path="image/matting" element={<div className='flex size-full'><SidebarNav key='sidebar-nav' /><ImageMatting /></div>} />
                </Route>
                <Route path="knowledge/chat" element={<InitHolder><ChatKnowledge /></InitHolder>} />
                <Route path="ws" element={<ChatWS />} />
                <Route path="/agreements">
                  <Route path=":type" element={<Agreement />}></Route>
                </Route>
                <Route path="*" element={<Error />} />
              </Routes>
            </BrowserRouter>
            <div id="nworld-app-modal-container"></div>
            {/* 误删，跳转技能广场弹框需要单独控制展示隐藏，需要单独一个id */}
            <div id="nworld-app-wcp-modal-container"></div>
            <iframe src="https://so.n.cn/?src=360so_onebox&fm=360bot" width={0} height={0}></iframe>
          </App>
        </ConfigProvider>
      </StyleProvider>
    </Suspense>
  );
});
